import 'reflect-metadata';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PrivyProvider } from '@privy-io/react-auth';
import store from '@/redux/root';
import logo from './assets/phenom-logo.svg';
import './assets/scss/themes.scss';
import './index.css';
import { PostHogProvider } from 'posthog-js/react';

import { Buffer } from 'buffer';
import { WagmiProvider } from 'wagmi';
import { wagmiConfig } from './wagmiConfig';
import { Environment, environment } from '@repo/environment';
import * as Sentry from '@sentry/react';
import { posthogHost } from '@repo/common-types/src/constants/index';
import { config } from '@repo/common-types/src/config';
import clientConfig from '@/common/config';

if (environment !== Environment.LOCAL) {
  Sentry.init({
    dsn: 'https://7d30fad41741f133182912d9cdede5ef@o4507074657386496.ingest.us.sentry.io/4510511587590144',
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: environment === Environment.PRODUCTION ? 0.1 : 1.0,
    tracePropagationTargets: [`*.phenompoker.com/api`],
    replaysSessionSampleRate: environment === Environment.PRODUCTION ? 0.1 : 1.0,
    replaysOnErrorSampleRate: environment === Environment.PRODUCTION ? 0.1 : 1.0,
    environment,
  });
}

global.Buffer = global.Buffer || Buffer;

// Create a query client
const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const options = {
  api_host: posthogHost,
};

root.render(
  <PostHogProvider apiKey={config.posthogPublicApiKey} options={options}>
    <PrivyProvider
      appId={clientConfig.privyAppId}
      config={{
        // Display email and wallet as login methods
        loginMethods: ['email'],
        // Customize Privy's appearance in your app
        appearance: {
          theme: 'dark',
          accentColor: '#A958D7',
          logo: logo,
        },
        // Create embedded wallets for users who don't have a wallet
        embeddedWallets: {
          createOnLogin: 'off',
        },
      }}
    >
      <Provider store={store}>
        <WagmiProvider config={wagmiConfig}>
          <QueryClientProvider client={queryClient}>
            <React.Fragment>
              <BrowserRouter basename={process.env.PUBLIC_URL}>
                <App />
              </BrowserRouter>
            </React.Fragment>
          </QueryClientProvider>
        </WagmiProvider>
      </Provider>
    </PrivyProvider>
  </PostHogProvider>,
);

reportWebVitals();
